import React, { type ReactNode, useRef } from "react";
import { Box, Text, Flex, AspectRatio, SimpleGrid } from "@chakra-ui/react";
import { type GetStaticProps, type NextPage } from "next";
import Image from "next/image";
import Marquee from "react-fast-marquee";
import got from "got";
import {
  getAnnouncementBanner,
  getPage,
  getStackedLogos,
  type Banner,
  type Page as SanityPage,
  type Customer,
} from "lib/sanity";
import { Section } from "components/section";
import { Page } from "components/page";
import { Values } from "components/values";
import {
  ActivityIcon,
  AirplaneIcon,
  BabyIcon,
  BarsIcon,
  BookIcon,
  CarIcon,
  FirstAidKitIcon,
  GlobeIcon,
  WalletIcon,
} from "components/careers-icons";
import { StackedLogos } from "components/logos";
import { Button } from "ui/button";
import { TextTitle } from "ui/text";
import { getPageMetaImage } from "utils";
import { blogBlurDataUrl } from "../constants/blur-data-urls";
import builtInBestPlacesAward from "../../public/assets/company/careers/built-in-award.png";
import sdOffsitePhoto from "../../public/assets/company/careers/sd-surfing.png";
import foundersPhoto from "../../public/assets/company/careers/founders.png";
import companyPhoto from "../../public/assets/company/careers/austin-company.png";
import austinDinnerPhoto from "../../public/assets/company/careers/austin-dinner.png";
import austinPicnicPhoto from "../../public/assets/company/careers/austin-picnic.png";

const marqueeImages = [
  {
    src: companyPhoto,
    alt: "Large group of team members.",
    transform: "rotate(1deg)",
  },
  {
    src: austinPicnicPhoto,
    alt: "Team members at a park in Austin for a company off-site event.",
    transform: "rotate(3deg)",
  },

  {
    src: austinDinnerPhoto,
    alt: "Team members at dinner in Austin for a company off-site event.",
    transform: "rotate(-1deg)",
  },
  {
    src: foundersPhoto,
    alt: "Founders of Hightouch.",
    transform: "rotate(-3deg)",
  },
  {
    src: sdOffsitePhoto,
    alt: "Team members on the beach with surfboards for a San Diego off-site event.",
    transform: "rotate(2deg)",
  },
];

type PageProps = {
  departments: FormattedDepartment[];
  page: SanityPage;
  announcementBanner?: Banner;
  customerRows: Customer[][];
};

type FormattedDepartment = {
  id: number;
  name: string;
  jobs: Array<{
    id: number;
    url: string;
    title: string;
    location: string;
  }>;
};

function BenefitItem({ icon, title, description }: { icon: ReactNode; title: string; description: string }) {
  return (
    <Box>
      {icon}
      <TextTitle title={title} titleAs="h3" description={description} size="sm" mt="small" />
    </Box>
  );
}

const Careers: NextPage<Readonly<PageProps>> = ({ departments, page, announcementBanner, customerRows }) => {
  const openPositionsSection = useRef<HTMLDivElement>(null);

  return (
    <Page
      title={page.metaTitle}
      description={page.metaDescription}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <Section center fullWidth my={{ base: 16, md: 20 }}>
        <Text as="h1" textStyle={{ base: "headline1", md: "display2" }} align="center" maxWidth="20ch">
          {page.title}
        </Text>

        <Button
          pulse="light"
          mt={10}
          onClick={() => {
            openPositionsSection.current?.scrollIntoView({
              behavior: "smooth",
            });
          }}
        >
          View open positions
        </Button>

        <Box mt={16} width="100%" sx={{ "> div": { mx: [-4, -4, -4, -4, 0], width: "auto" } }}>
          <Marquee pauseOnClick gradientWidth={0} speed={50} style={{ overflow: "hidden" }}>
            {marqueeImages.map((image) => (
              <Box key={image.alt} py={6} mx={{ base: 6, md: 10 }} transform={image.transform}>
                <AspectRatio
                  ratio={3 / 2}
                  width={{ base: "2xs", sm: "xs", lg: "md", xl: "lg" }}
                  fontSize={0}
                  pos="relative"
                >
                  <Image
                    priority
                    fill
                    sizes="100%"
                    src={image.src}
                    alt={image.alt}
                    style={{ objectFit: "contain" }}
                    placeholder="blur"
                    blurDataURL={blogBlurDataUrl}
                  />
                </AspectRatio>
              </Box>
            ))}
          </Marquee>
        </Box>

        <Box textStyle={{ base: "body3", md: "body2" }} mt={{ base: "biggest", md: "huge" }} maxWidth="3xl">
          <Text>
            We are a Series B startup backed by top investors including Amplify Partners, Bain Capital Ventures, ICONIQ
            Growth, and Y Combinator. Angel investors include the cofounders of Datadog, Okta, Segment, and Clearbit.
            Some of our customers include Autotrader, Plaid, Blend, Retool, Ramp, and CircleCI.
          </Text>

          <Text mt="small">
            Our founders were early employees of Segment where they learned about the problem space. At Segment, they
            were also introduced to the importance of the data warehouse, and to the many use cases that data could
            solve for all business teams.
          </Text>
        </Box>
      </Section>

      <StackedLogos
        customerRows={customerRows}
        title="We're proud to serve the most amazing companies out there"
        titleMaxW="4xl"
        bg="white"
        my={0}
        pt={{ base: "big", md: "biggest" }}
      />

      <Values />

      <Section bg="white">
        <Text as="h2" textStyle={{ base: "headline1", md: "display2" }} align="center">
          Benefits
        </Text>

        <SimpleGrid
          mt={{ base: "biggest", md: "huge" }}
          columns={{ base: 1, md: 2, lg: 3 }}
          justifyContent="space-between"
          gap={20}
        >
          <BenefitItem
            icon={<WalletIcon />}
            title="Competitive compensation"
            description="We offer competitive compensation and meaningful equity. We also offer 401k for our US employees and retirement plans for our international employees."
          />

          <BenefitItem
            icon={<GlobeIcon />}
            title="Hub & Remote friendly"
            description="Join our global team, either remotely or in one of our four in-person hubs. For those near a hub, enjoy complimentary lunches on Tuesdays and Thursdays."
          />

          <BenefitItem
            icon={<AirplaneIcon />}
            title="Flexible PTO"
            description="Downtime is just as important as on time and your teammates will support you while you relax and recharge."
          />

          <BenefitItem icon={<ActivityIcon />} title="Wellness" description="We offer wellness days each quarter." />

          <BenefitItem
            icon={<FirstAidKitIcon />}
            title="Core benefits"
            description="For full-time US-based employees, we cover all health benefit premiums, 80% for dependents, and life insurance."
          />

          <BenefitItem
            icon={<BabyIcon />}
            title="Parental leave"
            description="We value and support the family planning process. We provide up to 16 weeks for parents."
          />

          <BenefitItem
            icon={<BookIcon />}
            title="Professional development"
            description="We support employees for all learning resources needed to grow in their role (classes, books, conferences, etc)."
          />

          <BenefitItem
            icon={<BarsIcon />}
            title="Connectivity"
            description="We offer a $50 per month cell-phone or wifi connectivity stipend to all employees."
          />

          <BenefitItem
            icon={<CarIcon />}
            title="Commuter"
            description="Commuter benefits of $50 are offered to both employees who come into the offices and to remote workers working outside of their home."
          />
        </SimpleGrid>
      </Section>

      <Section center my="0">
        <Text
          ref={openPositionsSection}
          as="h2"
          id="open"
          align="center"
          textStyle={{ base: "headline1", md: "display2" }}
          scrollMarginTop={32}
        >
          <a href="#open">Open positions</a>
        </Text>

        <Flex
          mt={{ base: "biggest", md: "huge" }}
          width="4xl"
          maxWidth="100%"
          mx="auto"
          direction="column"
          gap={{ base: "bigger", md: "biggest" }}
        >
          {departments.map((department) => (
            <Box key={department.id}>
              <Text as="h3" textStyle={{ base: "headline3", md: "headline2" }} ml="tiny">
                {department.name}
              </Text>

              <Flex
                as="ul"
                mt="smaller"
                direction="column"
                listStyleType="none"
                gap={{ base: "smallest", lg: "tiniest" }}
              >
                {department.jobs.map((job) => (
                  <Box key={job.id} as="li">
                    <Flex
                      as="a"
                      role="group"
                      href={job.url}
                      target="_blank"
                      rel="noreferrer"
                      borderRadius="base"
                      direction={{ base: "column", lg: "row" }}
                      align={{ base: "start", lg: "center" }}
                      gap="tiny"
                      justify="space-between"
                      textStyle={{ base: "caption1", md: "body3" }}
                      position="relative"
                      p="tiny"
                      transition="all .15s ease-in-out"
                      _hover={{ bgColor: "brand.backgroundSecondary.light" }}
                      _focus={{
                        outline: "none !important",
                      }}
                      _focusVisible={{
                        boxShadow: "outline",
                      }}
                    >
                      <Text>{job.title}</Text>
                      <Text color="brand.textSecondary.light">{job.location}</Text>
                    </Flex>
                  </Box>
                ))}
              </Flex>
            </Box>
          ))}
        </Flex>
      </Section>
      <Section bg="white" maxW="4xl" mx="auto">
        <Flex align="center" flexDir={{ base: "column", md: "row" }} justify="center" gap="biggest" mr="smaller">
          <Text textStyle={{ base: "headline2", md: "headline1" }} maxW="xl">
            Hightouch has been named one of Built In’s Best Startup Places to Work in San Francisco
          </Text>
          <Box
            boxSize={{ base: "160px", lg: "180px" }}
            position="relative"
            zIndex={2}
            _after={{
              content: "''",
              position: "absolute",
              bottom: -4,
              right: -4,
              w: "100%",
              h: "100%",
              bg: "new.blue.150",
              zIndex: -1,
            }}
          >
            <Image
              fill
              sizes="100%"
              src={builtInBestPlacesAward}
              alt="Built-in Best Place"
              style={{ objectFit: "contain" }}
            />
          </Box>
        </Flex>
      </Section>
    </Page>
  );
};

export default Careers;

type Department = {
  id: number;
  name: string;
  jobs: Array<{
    id: number;
    title: string;
    location: {
      name: string;
    };
    absolute_url: string;
  }>;
};

const fetchDepartments = async () => {
  const response = await got<{ departments: Department[] }>(
    `https://boards-api.greenhouse.io/v1/boards/hightouch/departments`,
    {
      responseType: "json",
    },
  );

  return response.body.departments;
};

export const getStaticProps: GetStaticProps<PageProps> = async () => {
  const departments = await fetchDepartments();
  const [page, announcementBanner, customerRows] = await Promise.all([
    getPage("/careers"),
    getAnnouncementBanner(),
    getStackedLogos(),
  ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      page,
      announcementBanner,
      customerRows,
      departments: departments
        .filter((department) => department.id !== 0)
        .filter((department) => department.jobs.length > 0)
        .map((department) => ({
          id: department.id,
          name: department.name,
          jobs: department.jobs.map((job) => ({
            id: job.id,
            url: job.absolute_url,
            location: job.location.name,
            title: job.title,
          })),
        })),
    },
    revalidate: 60,
  };
};
